/**
 * What a person types is in their locale; what the parser wants is a dot.
 *
 * On a phone set to a locale that writes decimals with a comma, the numeric
 * keyboard offers a comma and no dot. "0,05" must mean five cents there, not
 * five dollars. The decimal separator is read from the device, and the input
 * is rewritten so that separator becomes a dot and the other becomes nothing.
 */
export function decimalSeparator(): '.' | ',' {
  try {
    const part = new Intl.NumberFormat(undefined)
      .formatToParts(1.1)
      .find((p) => p.type === 'decimal');
    return part?.value === ',' ? ',' : '.';
  } catch {
    return '.';
  }
}

export function normalizeDecimal(input: string, separator: '.' | ',' = decimalSeparator()): string {
  const text = input.trim();
  if (separator === ',') {
    // Comma is the decimal point; dots and spaces group thousands. But a lone
    // dot that cannot be grouping ("0.9", "1.5", "0.0000124") is a decimal
    // point typed by someone who thinks in dots, as most people pricing crypto
    // do. Reading "0.9" as 9 would post an offer at ten times the price meant.
    const oneDot =
      text.includes('.') && text.indexOf('.') === text.lastIndexOf('.') && !text.includes(',');
    if (oneDot && !/^[1-9]\d{0,2}\.\d{3}$/.test(text.replace(/\s/g, ''))) {
      return text.replace(/\s/g, '');
    }
    return text.replace(/[.\s]/g, '').replace(',', '.');
  }
  // Dot is the decimal point; commas and spaces group thousands. A lone comma
  // that cannot be grouping ("0,05", "0,123", "1,5") was meant as a decimal
  // point by someone whose keyboard had no dot; honour that. Reading "0,123"
  // as 123 would post an order a thousand times too big.
  const oneComma =
    text.includes(',') && text.indexOf(',') === text.lastIndexOf(',') && !text.includes('.');
  if (oneComma && !/^[1-9]\d{0,2},\d{3}$/.test(text.replace(/\s/g, ''))) {
    return text.replace(/\s/g, '').replace(',', '.');
  }
  return text.replace(/[,\s]/g, '');
}
